import { z } from 'zod';

/**
 * Tenant configuration keys. Every tenant-specific behaviour lives here (or in dedicated tables
 * such as leave policies / asset categories) — never hard-coded. Values are versioned in
 * `tenant_configurations` + `tenant_configuration_history`.
 */

const codeLabel = z.object({
  code: z.string().trim().min(1).max(40).regex(/^[A-Z0-9_]+$/, 'Use UPPER_SNAKE_CASE codes'),
  label: z.string().trim().min(1).max(80),
  active: z.boolean().default(true),
});

export const employeeNumberConfigSchema = z
  .object({
    mode: z.enum(['AUTO', 'MANUAL']),
    prefix: z.string().max(10).regex(/^[A-Za-z0-9-]*$/).default(''),
    width: z.number().int().min(1).max(10).default(4),
    /** NEVER = one continuous sequence; YEARLY = sequence per calendar year (prefix gets YYYY). */
    resetPolicy: z.enum(['NEVER', 'YEARLY']).default('NEVER'),
  })
  .strict();

export const listConfigSchema = z.array(codeLabel).max(100);

export const employeeRequiredFieldsSchema = z
  .array(
    z.enum([
      'middleName',
      'mobile',
      'designation',
      'departmentId',
      'teamId',
      'managerId',
      'probationEndDate',
      'workArrangement',
      'costCentre',
      'dateOfBirth',
      'personalEmail',
      'gender',
      'currentAddress',
      'permanentAddress',
    ]),
  )
  .max(20);

export const selfServiceConfigSchema = z
  .object({
    /** Fields the employee can edit directly. */
    editableFields: z.array(z.enum(['mobile', 'personalEmail', 'emergencyContacts', 'currentAddress', 'permanentAddress', 'preferredName'])).default([]),
    /** Controlled fields the employee may request a correction for (HR review required). */
    correctableFields: z
      .array(z.enum(['firstName', 'middleName', 'lastName', 'dateOfBirth', 'gender', 'bankAccount', 'governmentIds', 'designation']))
      .default([]),
  })
  .strict();

export const lifecycleConfigSchema = z
  .object({
    suspensionEnabled: z.boolean().default(false),
    noticePeriodStatusEnabled: z.boolean().default(false),
    /** IMMEDIATE = deactivate login when exit is recorded; ON_EXIT_DATE = scheduled on exit date. */
    accessRevocation: z.enum(['IMMEDIATE', 'ON_EXIT_DATE']).default('ON_EXIT_DATE'),
    /** Block exit while assets are still in the employee's custody (override needs permission + reason). */
    blockExitWithOutstandingAssets: z.boolean().default(true),
  })
  .strict();

export const retentionConfigSchema = z
  .object({
    auditRetentionDays: z.number().int().min(365).max(36500).default(2555),
    exitedEmployeeRetentionDays: z.number().int().min(0).max(36500).default(2555),
    importFileRetentionDays: z.number().int().min(1).max(3650).default(90),
  })
  .strict();

export const passwordPolicySchema = z
  .object({
    minLength: z.number().int().min(8).max(128).default(10),
    requireMixedCase: z.boolean().default(true),
    requireNumber: z.boolean().default(true),
    requireSymbol: z.boolean().default(false),
  })
  .strict();

export const TENANT_CONFIG_SCHEMAS = {
  'employee.number': employeeNumberConfigSchema,
  'employee.requiredFields': employeeRequiredFieldsSchema,
  'employee.employmentTypes': listConfigSchema,
  'employee.workArrangements': listConfigSchema,
  'employee.costCentres': listConfigSchema,
  'employee.lifecycle': lifecycleConfigSchema,
  'employee.selfService': selfServiceConfigSchema,
  'location.types': listConfigSchema,
  'retention': retentionConfigSchema,
  'security.passwordPolicy': passwordPolicySchema,
} as const;

export type TenantConfigKey = keyof typeof TENANT_CONFIG_SCHEMAS;
export type TenantConfigValue<K extends TenantConfigKey> = z.infer<(typeof TENANT_CONFIG_SCHEMAS)[K]>;

/**
 * Neutral defaults applied when a tenant is created. These are product defaults, not
 * mTouch policies; every one of them is editable by the tenant.
 */
export const TENANT_CONFIG_DEFAULTS: { [K in TenantConfigKey]: TenantConfigValue<K> } = {
  'employee.number': { mode: 'AUTO', prefix: 'EMP', width: 4, resetPolicy: 'NEVER' },
  'employee.requiredFields': [],
  'employee.employmentTypes': [
    { code: 'FULL_TIME', label: 'Full-time', active: true },
    { code: 'PART_TIME', label: 'Part-time', active: true },
    { code: 'CONTRACT', label: 'Contract', active: true },
    { code: 'INTERN', label: 'Intern', active: true },
  ],
  'employee.workArrangements': [
    { code: 'ONSITE', label: 'On-site', active: true },
    { code: 'HYBRID', label: 'Hybrid', active: true },
    { code: 'REMOTE', label: 'Remote', active: true },
  ],
  'employee.costCentres': [],
  'employee.lifecycle': {
    suspensionEnabled: false,
    noticePeriodStatusEnabled: false,
    accessRevocation: 'ON_EXIT_DATE',
    blockExitWithOutstandingAssets: true,
  },
  'employee.selfService': {
    editableFields: ['mobile', 'personalEmail', 'emergencyContacts', 'currentAddress', 'preferredName'],
    correctableFields: ['firstName', 'middleName', 'lastName', 'dateOfBirth', 'bankAccount', 'governmentIds'],
  },
  'location.types': [
    { code: 'HEAD_OFFICE', label: 'Head office', active: true },
    { code: 'BRANCH', label: 'Branch office', active: true },
    { code: 'WAREHOUSE', label: 'Warehouse', active: true },
    { code: 'CLIENT_SITE', label: 'Client site', active: true },
    { code: 'REMOTE_HUB', label: 'Remote hub', active: true },
  ],
  retention: { auditRetentionDays: 2555, exitedEmployeeRetentionDays: 2555, importFileRetentionDays: 90 },
  'security.passwordPolicy': { minLength: 10, requireMixedCase: true, requireNumber: true, requireSymbol: false },
};

export function validatePasswordAgainstPolicy(password: string, policy: TenantConfigValue<'security.passwordPolicy'>): string[] {
  const problems: string[] = [];
  if (password.length < policy.minLength) problems.push(`at least ${policy.minLength} characters`);
  if (password.length > 128) problems.push('at most 128 characters');
  if (policy.requireMixedCase && !(/[a-z]/.test(password) && /[A-Z]/.test(password))) problems.push('upper and lower case letters');
  if (policy.requireNumber && !/\d/.test(password)) problems.push('a number');
  if (policy.requireSymbol && !/[^A-Za-z0-9]/.test(password)) problems.push('a symbol');
  return problems;
}
